import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table as BsTable,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import type {
  LimitOffsetPaginationResponse,
  SerializedObject,
} from "../../../api/src/lib";

// Type definitions for security category asset class matrix
type SecurityCategoryAssetClassMatrix = {
  securityCategories: string[];
  assetClasses: string[];
  allocations: Record<string, Record<string, number>>;
};
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import TabContainerWithTabs from "../components/TabContainer";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import FirmNav from "./FirmNav";

type MappingChange = {
  securityCategory: string;
  assetClass: string;
  allocationPercent: number;
};

const FirmRiskSecurityCategory = () => {
  const roles = useRoles();
  const notificationContext = useContext(NotificationContext);
  const queryClient = useQueryClient();

  // Local state for edited allocations
  const [allocations, setAllocations] = useState<
    Record<string, Record<string, number>>
  >({});
  const [originalAllocations, setOriginalAllocations] = useState<
    Record<string, Record<string, number>>
  >({});
  const [changedMappings, setChangedMappings] = useState<MappingChange[]>([]);

  // Fetch the matrix data.
  //
  // NOT paginated, and not walked: this route returns a fixed object rather
  // than a list, so it passes criterion C8 and APP-1563 left it whole while
  // bounding the `PUT` that shares its path. It carries every security category
  // and every asset class in one response.
  const {
    isPending,
    isError,
    data: matrixData,
  } = useAuthenticatedFetch<{
    data: SerializedObject<SecurityCategoryAssetClassMatrix>;
  }>("/v1/firm/risk/security-category");

  const matrix = useMemo(
    () => matrixData?.data as SecurityCategoryAssetClassMatrix | undefined,
    [matrixData?.data],
  );

  // Initialize local state when data loads
  useEffect(() => {
    if (matrix) {
      setAllocations(matrix.allocations);
      setOriginalAllocations(matrix.allocations);
      setChangedMappings([]);
    }
  }, [matrix]);

  // A link from a security's page sets `?category=` (APP-1486). The matrix is
  // edited and saved whole, so the row is marked and scrolled to rather than
  // the other rows hidden.
  const [searchParams] = useSearchParams();
  const linkedCategories = useMemo(
    () => new Set(searchParams.getAll("category")),
    [searchParams],
  );
  const firstLinkedCategory = matrix?.securityCategories.find((category) =>
    linkedCategories.has(category),
  );
  const firstLinkedRow = useRef<HTMLTableRowElement | null>(null);
  const scrolledToLinkedRow = useRef(false);
  useEffect(() => {
    if (!scrolledToLinkedRow.current && firstLinkedRow.current) {
      scrolledToLinkedRow.current = true;
      firstLinkedRow.current.scrollIntoView?.({ block: "center" });
    }
  }, [firstLinkedCategory]);

  // Calculate row totals
  const getRowTotal = useCallback(
    (securityCategory: string): number => {
      if (!matrix || !allocations[securityCategory]) return 0;
      return matrix.assetClasses.reduce((sum, assetClass) => {
        return sum + (allocations[securityCategory]?.[assetClass] ?? 0);
      }, 0);
    },
    [allocations, matrix],
  );

  // Check if a row is valid (sums to 100)
  const isRowValid = useCallback(
    (securityCategory: string): boolean => {
      const total = getRowTotal(securityCategory);
      return Math.abs(total - 100) < 0.01;
    },
    [getRowTotal],
  );

  // Handle allocation change
  const handleAllocationChange = useCallback(
    (securityCategory: string, assetClass: string, value: number) => {
      const newAllocations = {
        ...allocations,
        [securityCategory]: {
          ...allocations[securityCategory],
          [assetClass]: value,
        },
      };
      setAllocations(newAllocations);

      // Track change
      const existingChangeIndex = changedMappings.findIndex(
        (c) =>
          c.securityCategory === securityCategory &&
          c.assetClass === assetClass,
      );

      const newChange: MappingChange = {
        securityCategory,
        assetClass,
        allocationPercent: value,
      };

      if (existingChangeIndex >= 0) {
        const newChanges = [...changedMappings];
        // Check if we're reverting to original
        if (originalAllocations[securityCategory]?.[assetClass] === value) {
          newChanges.splice(existingChangeIndex, 1);
        } else {
          newChanges[existingChangeIndex] = newChange;
        }
        setChangedMappings(newChanges);
      } else if (
        originalAllocations[securityCategory]?.[assetClass] !== value
      ) {
        setChangedMappings([...changedMappings, newChange]);
      }
    },
    [allocations, changedMappings, originalAllocations],
  );

  // Discard changes
  const handleDiscardChanges = useCallback(() => {
    if (matrix) {
      setAllocations(matrix.allocations);
      setOriginalAllocations(matrix.allocations);
      setChangedMappings([]);
    }
  }, [matrix]);

  // Submit mutation.
  //
  // Published at `/v1` by APP-1563, in the same change that bounded its echo —
  // the named change `docs/adr/firm-domain-admission.md` records against it on
  // criterion C8. Before that version landed,
  // `/v1/firm/risk/security-category` matched `PUT /v1/firm/risk/:id` with
  // `id = "security-category"` and was refused by `ParseIntPipe` with a 400.
  //
  // The echo is now a PAGE of the firm's security-category mappings, which is
  // what `pagination` beside `data` reports. This screen reads neither: it
  // refetches the fixed matrix at `/v1/firm/risk/security-category` instead,
  // which is not paginated and still carries every security category and every
  // asset class. The response type is stated in full anyway so a later reader
  // cannot take `data` here for the whole collection.
  const updateMappings = useAuthenticatedMutationAsync<{
    data: MappingChange[];
    pagination?: LimitOffsetPaginationResponse;
  }>(
    "/v1/firm/risk/security-category",
    async (data: { mappings: MappingChange[] }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const onSubmit = useMutation({
    mutationFn: async () => {
      // Validate all rows sum to 100
      const invalidRows = matrix?.securityCategories.filter(
        (sc) => !isRowValid(sc),
      );
      if (invalidRows && invalidRows.length > 0) {
        notificationContext.pushNotification({
          id: "firm-risk-security-category",
          header: "Validation Error",
          body: `The following security categories do not sum to 100%: ${invalidRows.join(", ")}`,
          variant: "danger",
        });
        return;
      }

      try {
        // The echo `updateMappings` resolves with is a bounded page (APP-1563)
        // and is deliberately discarded. The MATRIX read's key below is
        // unchanged by that job and deliberately so: `GET
        // /v1/firm/risk/security-category` returns a fixed object rather than a
        // list, passes criterion C8, is not paginated, and was versioned by an
        // earlier job. Only the `PUT` on the same path moved in this change,
        // and a mutation's url is not a query key.
        await updateMappings({ mappings: changedMappings });
        queryClient.invalidateQueries({
          queryKey: ["/v1/firm/risk/security-category"],
        });
        setChangedMappings([]);
        notificationContext.pushNotification({
          id: "firm-risk-security-category",
          header: "Security Category Mappings Updated",
          body: "Security category to asset class mappings updated successfully",
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save security category mappings", err);
        notificationContext.pushNotification({
          id: "firm-risk-security-category",
          header: "Failed to Save",
          body: "Security category mappings not saved",
          variant: "danger",
        });
      }
    },
  });

  const hasChanges = changedMappings.length > 0;
  const isAdmin = roles.includes("Admin");

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {isPending ? (
        <Loading />
      ) : isError ? (
        <Alert variant="danger">
          Failed to retrieve security category asset class mappings
        </Alert>
      ) : !matrix || matrix.securityCategories.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No Security Category Mappings Found</Alert.Heading>
          <p>
            No security category to asset class mappings have been configured
            for this firm.
          </p>
        </Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Security Category to Asset Class Mappings</h4>
              <p className="text-muted">
                Configure how each security category maps to asset classes for
                risk calculations. Each row must sum to 100%.
              </p>
            </Col>
          </Row>

          {isAdmin && hasChanges && (
            <Row className="mb-2">
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="success"
                  onClick={() => onSubmit.mutateAsync()}
                  disabled={onSubmit.isPending}
                >
                  Submit{" "}
                  {onSubmit.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    ""
                  )}
                </Button>
              </Col>
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="danger"
                  onClick={handleDiscardChanges}
                  disabled={onSubmit.isPending}
                >
                  Discard
                </Button>
              </Col>
            </Row>
          )}

          <Row>
            <Col
              style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}
            >
              <div style={{ overflowX: "auto" }}>
                <BsTable striped bordered hover>
                  <thead>
                    <tr>
                      <th>Security Category</th>
                      {matrix.assetClasses.map((assetClass) => (
                        <th key={assetClass} className="text-center">
                          {assetClass}
                        </th>
                      ))}
                      <th className="text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.securityCategories.map((securityCategory) => {
                      const total = getRowTotal(securityCategory);
                      const valid = isRowValid(securityCategory);
                      const linked = linkedCategories.has(securityCategory);

                      return (
                        <tr
                          key={securityCategory}
                          ref={
                            securityCategory === firstLinkedCategory
                              ? firstLinkedRow
                              : undefined
                          }
                          className={linked ? "table-primary" : undefined}
                          aria-current={linked ? "true" : undefined}
                        >
                          <td>{securityCategory}</td>
                          {matrix.assetClasses.map((assetClass) => (
                            <td
                              key={`${securityCategory}-${assetClass}`}
                              className="text-center"
                            >
                              {isAdmin ? (
                                <InputGroup
                                  style={{ width: "100px", margin: "auto" }}
                                >
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={
                                      allocations[securityCategory]?.[
                                        assetClass
                                      ] ?? 0
                                    }
                                    onChange={(e) =>
                                      handleAllocationChange(
                                        securityCategory,
                                        assetClass,
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    className={
                                      allocations[securityCategory]?.[
                                        assetClass
                                      ] !==
                                      originalAllocations[securityCategory]?.[
                                        assetClass
                                      ]
                                        ? "border-warning"
                                        : ""
                                    }
                                  />
                                  <InputGroup.Text>%</InputGroup.Text>
                                </InputGroup>
                              ) : (
                                <span>
                                  {allocations[securityCategory]?.[
                                    assetClass
                                  ] ?? 0}
                                  %
                                </span>
                              )}
                            </td>
                          ))}
                          <td className="text-center">
                            <Badge bg={valid ? "success" : "danger"}>
                              {total.toFixed(0)}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </BsTable>
              </div>
            </Col>
          </Row>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default FirmRiskSecurityCategory;
